import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { LogOut, RefreshCw, Trash2, Phone, Mail } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { LOGO_URL } from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  confirmed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

export default function AdminDashboard() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("rtf_admin_token")}` });

  const handleAuthError = useCallback(() => {
    localStorage.removeItem("rtf_admin_token");
    navigate("/admin/login");
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/reservations`, { headers: authHeaders() });
      setReservations(data);
    } catch (err) {
      if (err?.response?.status === 401) return handleAuthError();
      toast.error("Could not load reservations.");
    } finally {
      setLoading(false);
    }
  }, [handleAuthError]);

  useEffect(() => {
    if (!localStorage.getItem("rtf_admin_token")) return navigate("/admin/login");
    load();
  }, [load, navigate]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/reservations/${id}/status`, { status }, { headers: authHeaders() });
      setReservations((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Reservation ${status}.`);
    } catch (err) {
      if (err?.response?.status === 401) return handleAuthError();
      toast.error("Could not update status.");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/reservations/${id}`, { headers: authHeaders() });
      setReservations((rs) => rs.filter((r) => r.id !== id));
      toast.success("Reservation deleted.");
    } catch (err) {
      if (err?.response?.status === 401) return handleAuthError();
      toast.error("Could not delete reservation.");
    }
  };

  const logout = () => {
    localStorage.removeItem("rtf_admin_token");
    navigate("/admin/login");
  };

  const counts = {
    all: reservations.length,
    pending: reservations.filter((r) => r.status === "pending").length,
    confirmed: reservations.filter((r) => r.status === "confirmed").length,
    cancelled: reservations.filter((r) => r.status === "cancelled").length,
  };
  const visible = filter === "all" ? reservations : reservations.filter((r) => r.status === filter);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-[#0B3B39] text-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Resort The Forest logo" className="h-11 w-11 object-contain bg-[#FDFBF7] rounded-full p-0.5" />
            <div>
              <p className="font-serif-display text-lg leading-tight">Resort The Forest</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#C5A880]">Reservations Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs uppercase tracking-[0.2em] text-[#FDFBF7]/70 hover:text-[#C5A880] transition-colors hidden sm:block" data-testid="view-site-link">
              View Site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 border border-[#FDFBF7]/40 text-xs uppercase tracking-[0.2em] font-semibold px-5 py-2.5 rounded-sm hover:bg-[#FDFBF7]/10 transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-3" data-testid="status-filters">
            {["all", "pending", "confirmed", "cancelled"].map((s) => (
              <button
                key={s}
                data-testid={`filter-${s}-btn`}
                onClick={() => setFilter(s)}
                className={`text-xs uppercase tracking-[0.15em] font-semibold px-5 py-2.5 rounded-sm border transition-colors duration-300 ${
                  filter === s
                    ? "bg-[#0B3B39] text-[#FDFBF7] border-[#0B3B39]"
                    : "bg-white text-[#0B3B39] border-[#E6E2D8] hover:border-[#0B3B39]"
                }`}
              >
                {s} ({counts[s]})
              </button>
            ))}
          </div>
          <button
            onClick={load}
            data-testid="refresh-reservations-btn"
            className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold text-[#0B3B39] border border-[#E6E2D8] bg-white px-5 py-2.5 rounded-sm hover:border-[#0B3B39] transition-colors"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-[#4A5568] mt-12" data-testid="reservations-loading">Loading reservations...</p>
        ) : visible.length === 0 ? (
          <div className="mt-12 bg-white border border-[#E6E2D8] p-12 text-center" data-testid="reservations-empty">
            <p className="font-serif-display text-2xl text-[#0B3B39]">No reservations here yet.</p>
            <p className="text-sm text-[#4A5568] mt-2">New booking requests from the website will appear in this list.</p>
          </div>
        ) : (
          <div className="mt-8 flex flex-col gap-4">
            {visible.map((r) => (
              <article
                key={r.id}
                data-testid={`reservation-row-${r.id.slice(0, 8)}`}
                className="bg-white border border-[#E6E2D8] shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 flex flex-col lg:flex-row lg:items-center gap-6"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-serif-display text-xl text-[#0B3B39]">{r.guest_name}</h3>
                    <span className={`text-[10px] uppercase tracking-widest font-bold border px-2.5 py-1 ${STATUS_STYLES[r.status] || STATUS_STYLES.pending}`} data-testid={`reservation-status-${r.id.slice(0, 8)}`}>
                      {r.status}
                    </span>
                  </div>
                  <p className="text-sm text-[#1A1A1A] mt-2">
                    {(r.items || []).map((it) => `${it.rooms} × ${it.room_type}`).join(" + ")} · {r.check_in} &rarr; {r.check_out} · {r.nights} {r.nights === 1 ? "night" : "nights"} · {r.guests} {r.guests === 1 ? "guest" : "guests"}
                  </p>
                  <p className="text-sm text-[#4A5568] mt-1 flex flex-wrap gap-x-5 gap-y-1">
                    <span className="inline-flex items-center gap-1.5"><Phone size={13} className="text-[#C5A880]" />{r.phone}</span>
                    <span className="inline-flex items-center gap-1.5"><Mail size={13} className="text-[#C5A880]" />{r.email}</span>
                  </p>
                  {r.notes && <p className="text-sm text-[#4A5568] italic mt-2">&ldquo;{r.notes}&rdquo;</p>}
                  <p className="text-[10px] uppercase tracking-widest text-[#C5A880] mt-3">Ref {r.id.slice(0, 8).toUpperCase()}</p>
                </div>

                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  {r.status !== "confirmed" && (
                    <button
                      onClick={() => setStatus(r.id, "confirmed")}
                      data-testid={`confirm-btn-${r.id.slice(0, 8)}`}
                      className="text-xs uppercase tracking-[0.15em] font-semibold bg-[#0B3B39] text-[#FDFBF7] px-5 py-2.5 rounded-sm hover:bg-[#072A28] transition-colors"
                    >
                      Confirm
                    </button>
                  )}
                  {r.status !== "cancelled" && (
                    <button
                      onClick={() => setStatus(r.id, "cancelled")}
                      data-testid={`cancel-btn-${r.id.slice(0, 8)}`}
                      className="text-xs uppercase tracking-[0.15em] font-semibold border border-[#E6E2D8] text-[#4A5568] px-5 py-2.5 rounded-sm hover:border-red-300 hover:text-red-700 transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    onClick={() => remove(r.id)}
                    data-testid={`delete-btn-${r.id.slice(0, 8)}`}
                    aria-label="Delete reservation"
                    className="p-2.5 border border-[#E6E2D8] text-[#4A5568] rounded-sm hover:border-red-300 hover:text-red-700 transition-colors"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
